import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Navigation from "./components/Navigation";
import CartSidebar from "./components/CartSidebar";
import CheckoutModal from "./components/CheckoutModal";
import Hero from "./components/Hero";
import Shop from "./components/Shop";
import TrackSearch from "./components/TrackSearch";
import TrackingResult from "./components/TrackingResult";
import OrderConfirmation from "./components/OrderConfirmation";
import ReviewsSection from "./components/ReviewsSection";
import Footer from "./components/Footer";
import ReturnPolicy from "./components/ReturnPolicy";
import PrivacyPolicy from "./components/PrivacyPolicy";
import { Toaster } from "react-hot-toast";
import toast from "react-hot-toast";

import { ShoppingCart, Package, Truck, CheckCircle } from "lucide-react";
import p1 from "./pics/1.png";
import p2 from "./pics/2.png";
import p3 from "./pics/3.png";
import p4 from "./pics/4.png";
import p5 from "./pics/5.png";
import v1 from "./pics/v1.mp4";
import v2 from "./pics/v2.mp4";
import collagePhoto from "./pics/Collage.png";
import ScrollToTop from "./components/ScrollToTop";

export default function App() {
  const navigate = useNavigate();

  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutFormOpen, setCheckoutFormOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [selectedSize, setSelectedSize] = useState("M");
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFormAnimating, setIsFormAnimating] = useState(false);
  const [completedOrderId, setCompletedOrderId] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [showReturnPolicy, setShowReturnPolicy] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [newReview, setNewReview] = useState({
    name: "",
    rating: 5,
    comment: "",
  });
  const [customerDetails, setCustomerDetails] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    pincode: "",
  });

  const product = {
    id: 1,
    name: "ZipNZip Joggers",
    price: 1999,
    images: [p1, p2],
    imagesShop: [
      { src: p2, width: "w-[450px]", type: "image", loading: "lazy" },
      { src: p4, width: "w-[450px]", type: "image", loading: "lazy" },
      { src: p5, width: "w-[450px]", type: "image", loading: "lazy" },
      {
        src: p3,
        width: "w-full max-w-[550px] h-auto object-contain mt-[45px]",
        type: "image",
        loading: "lazy",
      },
      {
        src: collagePhoto,
        width: "w-full md:max-w-[800px] h-auto object-contain",
        type: "image",
        loading: "lazy",
      },
    ],
    prodVideos: [
      {
        src: v1,
        width:
          "w-full max-w-[650px] h-auto object-contain mb-[60px] mr-[2px] ml-[3px]",
        type: "video",
      },
      { src: v2, width: "w-[450px]", type: "video" },
    ],
    sizes: ["S", "M", "L"],
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/reviews`);
      const data = await res.json();
      setReviews(data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  const submitReview = async (photos = []) => {
    if (!newReview.name || !newReview.comment) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      const reviewData = {
        name: newReview.name,
        rating: newReview.rating,
        comment: newReview.comment,
        photos: photos,
      };

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reviewData),
      });

      if (res.ok) {
        toast.success("Review submitted! Thank you!");
        setNewReview({ name: "", rating: 5, comment: "" });
        fetchReviews();
      } else {
        toast.error("Failed to submit review");
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Failed to submit review. Please try again.");
    }
  };

  const trackOrderById = async (orderId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/track/${orderId}`,
      );
      if (res.ok) {
        const data = await res.json();
        setTrackingData(data);
        navigate("/tracking/" + orderId);
      } else {
        toast.error("Order not found. Please check your Order ID.");
      }
    } catch (error) {
      toast.error("Error tracking order");
    }
  };

  const addToCart = () => {
    const item = {
      ...product,
      selectedSize,
      quantity,
      cartId: Date.now(),
    };
    setCartItems([...cartItems, item]);
    setCartOpen(true);
  };

  const removeFromCart = (cartId) => {
    setCartItems(cartItems.filter((item) => item.cartId !== cartId));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [product.images.length]);

  const updateQuantity = (cartId, newQty) => {
    if (newQty < 1) return;
    setCartItems(
      cartItems.map((item) =>
        item.cartId === cartId ? { ...item, quantity: newQty } : item,
      ),
    );
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? product.images.length - 1 : prev - 1,
    );
  };

  const cartTotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  const openCheckoutForm = () => {
    if (cartItems.length === 0) {
      toast.error("Your cart is empty!");
      return;
    }
    setCheckoutFormOpen(true);
    setTimeout(() => setIsFormAnimating(true), 10);
  };

  const handleInputChange = (e) => {
    setCustomerDetails({
      ...customerDetails,
      [e.target.name]: e.target.value,
    });
  };

  const handleCheckout = async () => {
    if (
      !customerDetails.name ||
      !customerDetails.email ||
      !customerDetails.phone ||
      !customerDetails.address ||
      !customerDetails.pincode
    ) {
      toast.error("Please fill all fields");
      return;
    }

    if (customerDetails.phone.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    if (customerDetails.pincode.length !== 6) {
      toast.error("Please enter a valid 6-digit pincode");
      return;
    }

    setIsFormAnimating(false);
    await new Promise((resolve) => setTimeout(resolve, 300));
    setCheckoutFormOpen(false);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/create-order`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: cartTotal,
            customerDetails: customerDetails,
            items: cartItems.map((item) => ({
              name: item.name,
              size: item.selectedSize,
              quantity: item.quantity,
              price: item.price,
            })),
          }),
        },
      );

      const orderData = await response.json();
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "ZipNZip Store",
        description: "Checkout Payment",
        order_id: orderData.id,
        modal: {
          ondismiss: function () {
            toast.error(
              "Payment form closed. You can complete the payment anytime from your cart.",
            );
          },
        },

        handler: async function (razorpayResponse) {
          try {
            const verifyRes = await fetch(
              `${process.env.REACT_APP_API_URL}/api/verify-payment`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  razorpay_order_id: razorpayResponse.razorpay_order_id,
                  razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                  razorpay_signature: razorpayResponse.razorpay_signature,
                  orderId: orderData.orderId,
                  customerDetails: customerDetails,
                  items: cartItems.map((item) => ({
                    name: item.name,
                    size: item.selectedSize,
                    quantity: item.quantity,
                    price: item.price,
                  })),
                  amount: cartTotal,
                }),
              },
            );

            const verifyData = await verifyRes.json();

            if (verifyData.success) {
              setCompletedOrderId(verifyData.orderId);
              setCartItems([]);
              setCartOpen(false);
              setCustomerDetails({
                name: "",
                email: "",
                phone: "",
                address: "",
                pincode: "",
              });
              navigate("/confirmation/" + verifyData.orderId);
            } else {
              toast.error(
                "Payment verification failed. Please contact support if amount was deducted.",
              );
            }
          } catch (verifyError) {
            toast.error("Something went wrong while verifying payment.");
          }
        },
        prefill: {
          name: customerDetails.name,
          email: customerDetails.email,
          contact: customerDetails.phone,
        },
      };

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        const rzp = new window.Razorpay(options);
        rzp.open();
      };

      script.onerror = () => {
        toast.error("Failed to load Razorpay.");
      };
      document.body.appendChild(script);
    } catch (error) {
      toast.error("Checkout failed. Please try again.");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "confirmed":
        return <Package className="w-8 h-8" />;
      case "shipped":
        return <Truck className="w-8 h-8" />;
      case "delivered":
        return <CheckCircle className="w-8 h-8" />;
      default:
        return <Package className="w-8 h-8" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "pending":
        return "Order Placed";
      case "confirmed":
        return "Confirmed";
      case "shipped":
        return "Shipped";
      case "delivered":
        return "Delivered";
      default:
        return status;
    }
  };

  return (
    <>
      <Toaster position="top-center" />

      <ScrollToTop />

      <Navigation cartItems={cartItems} setCartOpen={setCartOpen} />

      <CartSidebar
        cartOpen={cartOpen}
        cartItems={cartItems}
        setCartOpen={setCartOpen}
        updateQuantity={updateQuantity}
        removeFromCart={removeFromCart}
        openCheckoutForm={openCheckoutForm}
        cartTotal={cartTotal}
      />

      <CheckoutModal
        checkoutFormOpen={checkoutFormOpen}
        isFormAnimating={isFormAnimating}
        setCheckoutFormOpen={setCheckoutFormOpen}
        setIsFormAnimating={setIsFormAnimating}
        handleInputChange={handleInputChange}
        handleCheckout={handleCheckout}
        customerDetails={customerDetails}
        cartTotal={cartTotal}
      />

      <Routes>
        <Route
          path="/"
          element={
            <Hero
              product={product}
              currentImageIndex={currentImageIndex}
              prevImage={prevImage}
              nextImage={nextImage}
            />
          }
        />

        <Route
          path="/shop"
          element={
            <>
              <Shop
                product={product}
                selectedSize={selectedSize}
                setSelectedSize={setSelectedSize}
                quantity={quantity}
                setQuantity={setQuantity}
                addToCart={addToCart}
                openCheckoutForm={openCheckoutForm}
              />

              <ReviewsSection
                reviews={reviews}
                newReview={newReview}
                setNewReview={setNewReview}
                submitReview={submitReview}
              />
            </>
          }
        />

        <Route
          path="/track"
          element={<TrackSearch trackOrderById={trackOrderById} />}
        />

        <Route
          path="/tracking/:orderId"
          element={
            <TrackingResult
              getStatusIcon={getStatusIcon}
              getStatusText={getStatusText}
            />
          }
        />

        <Route
          path="/confirmation/:completedOrderId"
          element={<OrderConfirmation />}
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* MODALS */}
      {showReturnPolicy && (
        <ReturnPolicy onClose={() => setShowReturnPolicy(false)} />
      )}

      {showPrivacyPolicy && (
        <PrivacyPolicy onClose={() => setShowPrivacyPolicy(false)} />
      )}

      {/* FOOTER */}
      <Footer
        openReturnPolicy={() => setShowReturnPolicy(true)}
        openPrivacyPolicy={() => setShowPrivacyPolicy(true)}
      />
    </>
  );
}
